import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentSubscription } from '../lib/stripe';
import type { Database } from '../lib/database.types';

type Profile = Database['public']['Tables']['profiles']['Row'];

export type User = {
  id: string;
  email: string;
  subscription_tier?: 'ESSENTIAL' | 'PREMIUM' | null;
  subscription_period?: 'monthly' | 'annual' | null;
  subscription_period_start?: number;
  subscription_period_end?: number;
  invoice_prefix?: string;
  quote_prefix?: string;
  next_invoice_number?: number;
  next_quote_number?: number;
} & Profile;

type AuthContextType = {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasActiveSubscription: boolean | null;
  updateUserLogo: (logo: string) => Promise<void>;
  updateProfile: (data: Partial<Profile>) => Promise<void>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshSubscription: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const checkSubscription = async () => {
    if (!user) {
      console.log('No user found, setting subscription to false');
      setHasActiveSubscription(false);
      return;
    }

    try {
      console.log('Fetching subscription status for user:', user.id);
      
      // First get the customer record to get the subscription tier and period
      const { data: customer, error: customerError } = await supabase
        .from('stripe_customers')
        .select('subscription_tier, subscription_period')
        .eq('user_id', user.id)
        .maybeSingle();

      if (customerError) {
        console.error('Error fetching customer:', customerError);
        throw customerError;
      }

      // Then get the subscription details
      const subscription = await getCurrentSubscription();
      console.log('Raw subscription data:', subscription);

      if (!subscription || !customer || customer.subscription_tier === null) {
        console.log('No subscription or customer found, or subscription_tier is null');
        setHasActiveSubscription(false);
        setUser(prev => prev ? { 
          ...prev, 
          subscription_tier: null,
          subscription_period: null 
        } : null);
        return;
      }

      const isActive = subscription.subscription_status === 'active';
      console.log('Subscription active status:', isActive);
      setHasActiveSubscription(isActive);

      setUser(prev => prev ? {
        ...prev,
        subscription_tier: customer.subscription_tier,
        subscription_period: customer.subscription_period,
        subscription_period_start: subscription.current_period_start,
        subscription_period_end: subscription.current_period_end,
      } : null);
    } catch (error) {
      console.error('Error checking subscription:', error);
      setHasActiveSubscription(false);
    }
  };

  useEffect(() => {
    checkSubscription();
  }, [user]);

  useEffect(() => {
    const {
      data: { subscription: authSubscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change event:', event);
      
      if (event === 'SIGNED_IN' && session?.user) {
        console.log('User signed in, fetching profile');
        await fetchProfile(session.user.id);
        if (location.pathname === '/login') {
          navigate('/app', { replace: true });
        }
      } else if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        console.log('User signed out or token refreshed');
        setUser(null);
        setHasActiveSubscription(null);
        if (location.pathname.startsWith('/app')) {
          navigate('/login', { replace: true, state: { from: location } });
        }
      }
    });

    // Set up subscription changes listener
    const subscriptionChannel = supabase
      .channel('stripe_subscription_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stripe_subscriptions',
        },
        async (payload) => {
          console.log('Subscription change detected:', payload);
          if (user) {
            await checkSubscription();
          }
        }
      )
      .subscribe();

    checkSession();

    return () => {
      authSubscription.unsubscribe();
      subscriptionChannel.unsubscribe();
    };
  }, [navigate, location.pathname]);

  const checkSession = async () => {
    try {
      console.log('Checking session');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.log('No active session found');
        setUser(null);
        setHasActiveSubscription(null);
        setIsLoading(false);
        if (location.pathname.startsWith('/app')) {
          navigate('/login', { replace: true, state: { from: location } });
        }
        return;
      }

      const { error } = await supabase.auth.getUser();
      if (error) {
        throw error;
      }

      await fetchProfile(session.user.id);
    } catch (error) {
      console.error('Error checking session:', error);
      setUser(null);
      setHasActiveSubscription(null);
      if (location.pathname.startsWith('/app')) {
        navigate('/login', { replace: true, state: { from: location } });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProfile = async (userId: string) => {
    try {
      console.log('Fetching profile for user:', userId);
      
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) throw profileError;

      // If no profile exists, create one
      if (!profile) {
        const { data: { user: authUser }, error: userError } = await supabase.auth.getUser();
        if (userError || !authUser) throw userError;

        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert([{ id: userId }])
          .select()
          .maybeSingle();

        if (createError) throw createError;
        if (!newProfile) throw new Error('Failed to create profile');

        const userData = {
          ...newProfile,
          id: userId,
          email: authUser.email || '',
          subscription_tier: null,
          subscription_period: null
        };

        console.log('Setting new user data:', userData);
        setUser(userData);
        setHasActiveSubscription(false);
        return;
      }

      const { data: { user: authUser }, error: userError } = await supabase.auth.getUser();
      if (userError || !authUser) throw userError;

      // Get customer information
      const { data: customer, error: customerError } = await supabase
        .from('stripe_customers')
        .select('subscription_tier, subscription_period')
        .eq('user_id', userId)
        .maybeSingle();

      if (customerError) throw customerError;

      // Get subscription information
      console.log('Fetching subscription information');
      const subscription = await getCurrentSubscription();
      console.log('Raw subscription data:', subscription);

      const userData = {
        ...profile,
        id: userId,
        email: authUser.email || '',
        subscription_tier: customer?.subscription_tier || null,
        subscription_period: customer?.subscription_period || null,
        subscription_period_start: subscription?.current_period_start,
        subscription_period_end: subscription?.current_period_end,
      };

      console.log('Setting user data:', userData);
      setUser(userData);

      const isActive = subscription?.subscription_status === 'active';
      console.log('Setting subscription status:', isActive);
      setHasActiveSubscription(!!customer);

    } catch (error) {
      console.error('Error fetching profile:', error);
      setUser(null);
      setHasActiveSubscription(null);
      if (location.pathname.startsWith('/app')) {
        navigate('/login', { replace: true, state: { from: location } });
      }
    }
  };

  const signup = async (email: string, password: string, name: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
        },
      },
    });

    if (error) throw error;
    if (!data.user) throw new Error('No user data returned');
  };

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setHasActiveSubscription(null);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
      throw error;
    }
  };

  const updateUserLogo = async (logo: string) => {
    if (!user) throw new Error('No user logged in');

    const { error } = await supabase
      .from('profiles')
      .update({ logo, updated_at: new Date().toISOString() })
      .eq('id', user.id);

    if (error) throw error;

    setUser(prev => prev ? { ...prev, logo } : null);
  };

  const updateProfile = async (data: Partial<Profile>) => {
    if (!user) throw new Error('No user logged in');

    const { error } = await supabase
      .from('profiles')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', user.id);

    if (error) throw error;

    setUser(prev => prev ? { ...prev, ...data } : null);
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw error;
  };

  const refreshSubscription = async () => {
    await checkSubscription();
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        signup, 
        login, 
        logout, 
        isAuthenticated: !!user, 
        isLoading,
        hasActiveSubscription,
        updateUserLogo,
        updateProfile,
        updatePassword,
        refreshSubscription
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};